import { useState } from "react";
import { Icon, Avatar, Modal, Field, Inp, Sel, Txt, SaveBtn, inputStyle } from "../shared/ui.jsx";
import { USER_COLORS, USERS } from "../shared/constants.js";
import { today, displayDate } from "../shared/utils";

const PROJECTS = ["Litvill Lessons", "Litvill Lab", "Both"];
const PARTNER_TYPES = ["University", "School", "NGO", "Company", "Government", "Media", "Individual", "Other"];
const STATUSES = ["Contacted", "Interested", "MoU signed", "Active", "Passive", "Dropped"];
const STATUS_COLORS = {
  "Contacted":   "#8a7a60",
  "Interested":  "#3d6b8a",
  "MoU signed":  "#5b8a6b",
  "Active":      "#4a8a6a",
  "Passive":     "#b8a890",
  "Dropped":     "#b05050",
};

export function PartnerForm({ onSave, onClose, existing, user }) {
  const [form, setForm] = useState(existing || {
    name: "", type: "University", project: "Litvill Lessons",
    country: "", city: "", website: "",
    contactName: "", contactRole: "", contactEmail: "", contactPhone: "",
    status: "Contacted", notes: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = () => {
    if (!form.name.trim()) return;
    onSave({
      ...form,
      id: existing?.id || Date.now(),
      createdAt: existing?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      updatedBy: user,
      log: existing?.log || [],
    });
    onClose();
  };
  return (
    <>
      <Field label="Organisation name *"><Inp value={form.name} onChange={e => set("name", e.target.value)} placeholder="Presidency University, Chitkara…" /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Type"><Sel value={form.type} onChange={e => set("type", e.target.value)}>{PARTNER_TYPES.map(t => <option key={t}>{t}</option>)}</Sel></Field>
        <Field label="Project"><Sel value={form.project} onChange={e => set("project", e.target.value)}>{PROJECTS.map(p => <option key={p}>{p}</option>)}</Sel></Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Country"><Inp value={form.country} onChange={e => set("country", e.target.value)} placeholder="India" /></Field>
        <Field label="City"><Inp value={form.city} onChange={e => set("city", e.target.value)} placeholder="Bangalore" /></Field>
      </div>
      <Field label="Website"><Inp value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://…" /></Field>

      <div style={{ borderTop: "1px solid var(--ink10)", margin: "1rem 0 0.75rem", paddingTop: "0.75rem", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ink50)" }}>Contact person</div>
      <Field label="Name"><Inp value={form.contactName} onChange={e => set("contactName", e.target.value)} placeholder="Dr. Sharma" /></Field>
      <Field label="Role"><Inp value={form.contactRole} onChange={e => set("contactRole", e.target.value)} placeholder="Dean, ISR Cell coordinator…" /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Email"><Inp type="email" value={form.contactEmail} onChange={e => set("contactEmail", e.target.value)} /></Field>
        <Field label="Phone"><Inp value={form.contactPhone} onChange={e => set("contactPhone", e.target.value)} /></Field>
      </div>

      <div style={{ borderTop: "1px solid var(--ink10)", margin: "1rem 0 0.75rem", paddingTop: "0.75rem", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ink50)" }}>Status</div>
      <Field label="Status"><Sel value={form.status} onChange={e => set("status", e.target.value)}>{STATUSES.map(s => <option key={s}>{s}</option>)}</Sel></Field>
      <Field label="Notes"><Txt value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="How we met, what was agreed…" /></Field>
      <SaveBtn onClick={save} />
    </>
  );
}

function LogEntryForm({ onSave, onClose, user }) {
  const [text, setText] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const save = () => {
    if (!text.trim()) return;
    onSave({ id: Date.now(), date: today(), text, newStatus: newStatus || null, user });
    onClose();
  };
  return (
    <>
      <Field label="What happened">
        <Txt value={text} onChange={e => setText(e.target.value)} placeholder="Had a call, discussed MoU terms. They want to start in January…" />
      </Field>
      <Field label="Update status (optional)">
        <Sel value={newStatus} onChange={e => setNewStatus(e.target.value)}>
          <option value="">— keep current —</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </Sel>
      </Field>
      <SaveBtn onClick={save} label="Add to log" />
    </>
  );
}

function PartnerCard({ partner, onEdit, onDelete, onLog, currentUser }) {
  const [expanded, setExpanded] = useState(false);
  const statusColor = STATUS_COLORS[partner.status] || "var(--ink50)";
  const projectColor = partner.project === "Litvill Lessons" ? "#3d6b8a" : partner.project === "Litvill Lab" ? "#5b8a6b" : "#7a5c2e";

  return (
    <div style={{ background: "var(--paper-dark)", borderRadius: 4, marginBottom: "0.6rem", border: "1px solid var(--ink10)", overflow: "hidden" }}>
      {/* Header — always visible */}
      <div style={{ padding: "0.85rem 1rem", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }} onClick={() => setExpanded(v => !v)}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", marginBottom: 4 }}>
            <span style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.08em", color: projectColor, fontWeight: 600 }}>{partner.project}</span>
            <span style={{ fontSize: "0.65rem", color: "var(--ink30)" }}>·</span>
            <span style={{ fontSize: "0.65rem", color: "var(--ink50)" }}>{partner.type}</span>
            {partner.country && <span style={{ fontSize: "0.65rem", color: "var(--ink30)" }}>· {partner.city ? `${partner.city}, ` : ""}{partner.country}</span>}
          </div>
          <div style={{ fontFamily: "var(--font-head)", fontSize: "1rem", color: "var(--ink)" }}>{partner.name}</div>
          {partner.contactName && (
            <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginTop: 2 }}>
              👤 {partner.contactName}{partner.contactRole ? ` · ${partner.contactRole}` : ""}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0, marginLeft: "0.5rem" }}>
          <span style={{ fontSize: "0.7rem", padding: "2px 8px", borderRadius: 10, background: statusColor + "22", color: statusColor, fontWeight: 600, whiteSpace: "nowrap" }}>{partner.status}</span>
          <Icon name={expanded ? "chevronUp" : "chevron"} size={14} />
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ borderTop: "1px solid var(--ink10)", padding: "0.85rem 1rem" }}>
          {/* Contact info */}
          {(partner.contactEmail || partner.contactPhone || partner.website) && (
            <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginBottom: "0.75rem", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              {partner.contactEmail && <a href={`mailto:${partner.contactEmail}`} style={{ color: "var(--accent)", textDecoration: "none" }}>{partner.contactEmail}</a>}
              {partner.contactPhone && <span>{partner.contactPhone}</span>}
              {partner.website && <a href={partner.website} target="_blank" rel="noreferrer" style={{ color: "var(--accent)", textDecoration: "none" }}>{partner.website}</a>}
            </div>
          )}

          {/* Notes */}
          {partner.notes && (
            <div style={{ fontSize: "0.82rem", color: "var(--ink70)", fontStyle: "italic", marginBottom: "0.75rem" }}>{partner.notes}</div>
          )}

          {/* Activity log */}
          {partner.log && partner.log.length > 0 && (
            <div style={{ marginBottom: "0.75rem" }}>
              <div style={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ink30)", marginBottom: "0.4rem" }}>Activity log</div>
              {[...partner.log].reverse().map(entry => (
                <div key={entry.id} style={{ padding: "0.5rem 0", borderTop: "1px solid var(--ink10)", display: "flex", gap: "0.6rem" }}>
                  <div style={{ flexShrink: 0, paddingTop: 2 }}><Avatar user={entry.user} size={16} /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.68rem", color: "var(--ink30)", marginBottom: 2 }}>
                      {displayDate(entry.date)}
                      {entry.newStatus && <span style={{ marginLeft: 6, color: STATUS_COLORS[entry.newStatus], fontWeight: 600 }}>→ {entry.newStatus}</span>}
                    </div>
                    <div style={{ fontSize: "0.82rem", color: "var(--ink70)" }}>{entry.text}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button onClick={() => onLog(partner)} style={{ fontSize: "0.78rem", padding: "0.3rem 0.7rem", border: "1px solid var(--accent)", borderRadius: 3, cursor: "pointer", background: "transparent", color: "var(--accent)" }}>+ Log update</button>
            <button onClick={() => onEdit(partner)} style={{ fontSize: "0.78rem", padding: "0.3rem 0.7rem", border: "1px solid var(--ink20)", borderRadius: 3, cursor: "pointer", background: "transparent", color: "var(--ink50)" }}>Edit</button>
            <button onClick={() => onDelete(partner.id)} style={{ fontSize: "0.78rem", padding: "0.3rem 0.7rem", border: "1px solid var(--ink20)", borderRadius: 3, cursor: "pointer", background: "transparent", color: "var(--ink30)" }}>Delete</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PartnersTab({ user, partners, setPartners, searchQ }) {
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [logTarget, setLogTarget] = useState(null);
  const [filterProject, setFilterProject] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  let items = partners;
  if (searchQ) items = items.filter(p =>
    p.name.toLowerCase().includes(searchQ) ||
    (p.contactName || "").toLowerCase().includes(searchQ) ||
    (p.city || "").toLowerCase().includes(searchQ) ||
    (p.country || "").toLowerCase().includes(searchQ) ||
    (p.notes || "").toLowerCase().includes(searchQ) ||
    (p.log || []).some(l => l.text.toLowerCase().includes(searchQ))
  );
  if (filterProject !== "all") items = items.filter(p => p.project === filterProject);
  if (filterStatus !== "all") items = items.filter(p => p.status === filterStatus);

  const save = (item) => {
    if (editItem) setPartners(partners.map(p => p.id === item.id ? item : p));
    else setPartners([...partners, item]);
    setEditItem(null); setShowForm(false);
  };

  const addLog = (partner, entry) => {
    const updated = {
      ...partner,
      log: [...(partner.log || []), entry],
      status: entry.newStatus || partner.status,
      updatedAt: new Date().toISOString(),
      updatedBy: user,
    };
    setPartners(partners.map(p => p.id === partner.id ? updated : p));
    setLogTarget(null);
  };

  const del = (id) => setPartners(partners.filter(p => p.id !== id));

  return (
    <div>
      {/* Filters */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        <Sel value={filterProject} onChange={e => setFilterProject(e.target.value)} style={{ ...inputStyle, width: "auto", fontSize: "0.8rem" }}>
          <option value="all">All projects</option>
          {PROJECTS.map(p => <option key={p}>{p}</option>)}
        </Sel>
        <Sel value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, width: "auto", fontSize: "0.8rem" }}>
          <option value="all">All statuses</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </Sel>
      </div>

      <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginBottom: "0.5rem" }}>{items.length} partner(s) · shared</div>
      {items.length === 0 && <p style={{ color: "var(--ink30)", fontStyle: "italic", fontSize: "0.9rem" }}>No partners found.</p>}

      {items.map(p => (
        <PartnerCard key={p.id} partner={p} currentUser={user}
          onEdit={item => { setEditItem(item); setShowForm(true); }}
          onDelete={del}
          onLog={item => setLogTarget(item)}
        />
      ))}

      {showForm && (
        <Modal title={editItem ? "Edit Partner" : "New Partner"} onClose={() => { setShowForm(false); setEditItem(null); }}>
          <PartnerForm user={user} onSave={save} onClose={() => { setShowForm(false); setEditItem(null); }} existing={editItem} />
        </Modal>
      )}

      {logTarget && (
        <Modal title={`Log update · ${logTarget.name}`} onClose={() => setLogTarget(null)}>
          <LogEntryForm user={user} onSave={(entry) => addLog(logTarget, entry)} onClose={() => setLogTarget(null)} />
        </Modal>
      )}
    </div>
  );
}